<script lang="ts" setup>
import { ref, watch, computed } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import {
  OrganizationCreateDocument,
  OrganizationUpdateDocument,
  OrganizationType,
} from '@/gql/graphql'; // from codegen
import {
  BAlert,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BSpinner,
} from 'bootstrap-vue-next';
import { CountrySelectOptions } from '@/utils/cloud-utils';

interface Props {
  organization?: Partial<OrganizationType>;
  headerText?: string;
  editable?: boolean;
  buttonText?: string;
  showAdminFields?: boolean;
}
const props = defineProps<Props>();
const emits = defineEmits(['organizationCreated', 'organizationUpdated']);

// Determine the country selection validation state
const showValidation = ref<boolean>(false);
const countryValidationState = computed(() => {
  if (showValidation.value) {
    return formValues.value.addressCountryCode !== null;
  }
  return null;
});

// local form values (extended organization type)
export interface IOrganizationFormValues extends OrganizationType {
  ownerEmail: string | null;
  clearSalesforceId: boolean;
}

const formValues = ref<Partial<IOrganizationFormValues>>({});

// Watch the incoming org => update local form
watch(
  () => props.organization,
  (newVal) => {
    formValues.value = {
      name: newVal?.name || '',
      salesforceId: newVal?.salesforceId,
      addressStreet: newVal?.addressStreet || '',
      addressCity: newVal?.addressCity || '',
      addressState: newVal?.addressState || '',
      addressPostalCode: newVal?.addressPostalCode || '',
      addressCountryCode: newVal?.addressCountryCode || '',
    };
  },
  { immediate: true }
);

// --- Create/Update Organization Mutations ---
const mutationInProgress = ref(false);
const mutationShowSuccess = ref(false);
const mutationError = ref<string | null>(null);

const {
  mutate: createOrganizationMut,
  onDone: onCreateOrganizationDone,
  onError: onCreateOrganizationError,
} = useMutation(OrganizationCreateDocument);

const {
  mutate: updateOrganizationMut,
  onDone: onUpdateOrganizationDone,
  onError: onUpdateOrganizationError,
} = useMutation(OrganizationUpdateDocument);

onCreateOrganizationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.organizationCreate) {
    console.debug('Organization created:', res.data.organizationCreate);
    mutationShowSuccess.value = true;
    emits('organizationCreated');
    setTimeout(() => {
      mutationShowSuccess.value = false;
    }, 1000);
  } else {
    console.error('Organization creation failed:', res.data);
    mutationError.value = 'Organization creation failed: ' + res.data;
  }
});

onCreateOrganizationError((err) => {
  console.error('Error creating organization:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

onUpdateOrganizationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.organizationUpdate) {
    console.debug('Organization updated:', res.data.organizationUpdate);
    mutationShowSuccess.value = true;
    emits('organizationUpdated');
    setTimeout(() => {
      mutationShowSuccess.value = false;
    }, 1000);
  } else {
    console.error('Organization update failed:', res.data);
    mutationError.value = 'Organization update failed: ' + res.data;
  }
});

onUpdateOrganizationError((err) => {
  console.error('Error updating organization:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handlers
function onSubmit() {
  showValidation.value = true;
  if (countryValidationState.value) {
    showValidation.value = false;

    // Check orgCreateInput.ownerEmail is not empty string, otherwise use null
    if (!formValues.value.ownerEmail) {
      formValues.value.ownerEmail = null;
    }

    // Check Salesforce ID is not empty string, otherwise use null
    if (!formValues.value.salesforceId) {
      formValues.value.salesforceId = null;
      formValues.value.clearSalesforceId = true;
    } else {
      formValues.value.clearSalesforceId = false;
      try {
        formValues.value.salesforceId = Number(formValues.value.salesforceId);
      } catch (e) {
        mutationError.value = 'Salesforce ID must be a number';
        console.error('Salesforce ID must be a number');
        return;
      }
    }

    // Create or update organization
    mutationInProgress.value = true;
    mutationError.value = null;

    if (!props.organization) {
      console.log('Creating organization:', formValues.value);
      createOrganizationMut({
        ownerEmail: formValues.value.ownerEmail,
        name: formValues.value.name,
        salesforceId: formValues.value.salesforceId,
        addressStreet: formValues.value.addressStreet,
        addressCity: formValues.value.addressCity,
        addressState: formValues.value.addressState,
        addressPostalCode: formValues.value.addressPostalCode,
        addressCountryCode: formValues.value.addressCountryCode,
      });
    } else {
      console.log('Updating organization:', formValues.value);
      updateOrganizationMut({
        id: props.organization.id,
        name: formValues.value.name,
        salesforceId: formValues.value.salesforceId,
        clearSalesforceId: formValues.value.clearSalesforceId,
        addressCity: formValues.value.addressCity,
        addressCountryCode: formValues.value.addressCountryCode,
        addressPostalCode: formValues.value.addressPostalCode,
        addressState: formValues.value.addressState,
        addressStreet: formValues.value.addressStreet,
      });
    }
  }
}
</script>

<template>
  <div class="organization-form">
    <h4 v-if="props.headerText">
      <font-awesome-icon :icon="['fas', 'building']" class="me-2" />
      {{ props.headerText }}
    </h4>
    <b-form @submit.prevent="onSubmit">
      <!-- Organization owner -->
      <b-form-group
        v-if="props.showAdminFields && !props.organization"
        label="Organization`s Owner E-mail"
        label-class="pb-0 mb-0 mt-1"
      >
        <b-form-input
          :disabled="!props.editable"
          v-model="formValues.ownerEmail"
          name="organization-owner-email"
        />
        <div v-if="props.editable" class="mb-3">
          <small class="text-secondary" variant="secondary">
            <ol>
              <li>
                Fill in if you want to
                <strong>delegate organization ownership</strong>, otherwise
                <strong>you will be the owner</strong>.
              </li>
              <li>
                The user <strong>does not need to be registered</strong> on the
                platform, email invite will be sent.
              </li>
              <li>
                The suggested organization owner
                <strong>can be part of other organization</strong>. Users can
                switch between organizations they are part of.
              </li>
            </ol>
          </small>
        </div>
      </b-form-group>
      <!-- Organization name -->
      <b-form-group label="Organization Name" label-class="pb-0 mb-0 mt-1">
        <b-form-input
          :disabled="!props.editable"
          v-model="formValues.name"
          name="organization-name"
          required
        />
      </b-form-group>
      <!-- Organization name -->
      <b-form-group
        v-if="props.showAdminFields"
        label="Salesforce ID"
        label-class="pb-0 mb-0 mt-1"
      >
        <b-form-input
          :disabled="!props.editable"
          v-model="formValues.salesforceId"
          name="salesforce-id"
          type="number"
        />
      </b-form-group>
      <!-- Street -->
      <b-form-group label="Street" label-class="pb-0 mb-0 mt-1">
        <b-form-input
          :disabled="!props.editable"
          v-model="formValues.addressStreet"
          name="organization-address-street"
          required
        />
      </b-form-group>

      <!-- City -->
      <b-form-group label="City" label-class="pb-0 mb-0 mt-1">
        <b-form-input
          :disabled="!props.editable"
          v-model="formValues.addressCity"
          name="organization-address-city"
          required
        />
      </b-form-group>

      <!-- State -->
      <b-form-group label="State" label-class="pb-0 mb-0 mt-1">
        <b-form-input
          :disabled="!props.editable"
          v-model="formValues.addressState"
          name="organization-address-state"
        />
      </b-form-group>

      <!-- Postal Code -->
      <b-form-group label="Postal Code" label-class="pb-0 mb-0 mt-1">
        <b-form-input
          :disabled="!props.editable"
          v-model="formValues.addressPostalCode"
          name="organization-address-postal-code"
          required
        />
      </b-form-group>

      <b-form-group
        label="Country"
        :state="countryValidationState"
        label-class="pb-0 mb-0 mt-1"
      >
        <b-form-select
          :disabled="!props.editable"
          :options="CountrySelectOptions"
          :state="countryValidationState"
          v-model="formValues.addressCountryCode"
          name="organization-address-country"
          required
        />
      </b-form-group>

      <b-button
        v-if="props.editable"
        variant="primary"
        type="submit"
        class="mt-3"
      >
        <font-awesome-icon :icon="['fas', 'plus']" class="me-1" />
        <span v-if="mutationInProgress">
          Saving Changes
          <b-spinner small label=""></b-spinner>
        </span>
        <span v-else-if="mutationShowSuccess">
          Saved
          <font-awesome-icon :icon="['fa', 'check-double']" class="me-2"
        /></span>
        <span v-else>
          {{ props.buttonText || 'Save' }}
        </span>
      </b-button>
    </b-form>
    <BAlert v-model="mutationError" variant="danger" dismissible class="mt-3">
      {{ mutationError }}
    </BAlert>
  </div>
</template>
